import {useEffect, useRef, useState} from 'react';
import {
  ANNOUNCE_EVENT,
  AnnounceEventDetail,
  AnnouncePoliteness,
} from './announce';

/**
 * Invisible, not vocalized by screen readers. Toggled on every flush so the
 * rendered text always actually changes even when the same message is
 * announced twice in a row — otherwise React sees no text-node change and
 * skips the DOM write, so AT never re-reads it. Bounded: either present once
 * or absent, never accumulates across announcements.
 */
export const ZERO_WIDTH_SPACE = '​';

export const ANNOUNCER_DEBOUNCE_MS = 350;

export interface AnnouncerSlotState {
  message: string;
}

interface InternalState {
  message: string;
  hasSuffix: boolean;
}

const initialState: InternalState = {message: '', hasSuffix: false};

/**
 * Listens for `announce()` events at one politeness level and returns the
 * current message for it
 */
export const useAnnouncerSlot = (
  politeness: AnnouncePoliteness,
): AnnouncerSlotState => {
  const [state, setState] = useState<InternalState>(initialState);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleAnnounce = (event: Event) => {
      const detail = (event as CustomEvent<AnnounceEventDetail>).detail;

      if (detail.politeness !== politeness) {
        return;
      }

      // Classic debounce: every new event restarts the wait, so only the last
      // message of a burst is spoken, once things have been quiet for a moment.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setState(prev => ({
          message: detail.message,
          hasSuffix: !prev.hasSuffix,
        }));
        timeoutRef.current = null;
      }, ANNOUNCER_DEBOUNCE_MS);
    };

    document.addEventListener(ANNOUNCE_EVENT, handleAnnounce);

    return () => {
      document.removeEventListener(ANNOUNCE_EVENT, handleAnnounce);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [politeness]);

  return {
    message: state.hasSuffix
      ? `${state.message}${ZERO_WIDTH_SPACE}`
      : state.message,
  };
};
